'use client'

import { Tooltip as TooltipPrimitive } from '@base-ui/react/tooltip'
import { useId, type ReactElement } from 'react'
import type { TestIdProps } from '../internal/test-id'
import { OVERLAY_LAYER } from '../internal/layer'

/**
 * A tooltip, from Base UI.
 *
 * Deliberately limited to text. A tooltip is not reachable by keyboard on touch
 * devices and is skipped by some screen reader modes, so anything a user must
 * read to complete a task belongs in a Field hint instead. Keeping the content
 * type to `string` makes that hard to ignore.
 *
 * Base UI does not set `role="tooltip"` or wire `aria-describedby` on its own,
 * so a tooltip built straight from the primitive is visible on screen and
 * invisible to assistive technology. Both are supplied here, which is the whole
 * reason this component exists rather than each app composing the primitive.
 *
 * Provider is exported separately because delay behaviour is an app-level
 * choice: one provider near the root, not one per tooltip.
 */
export const TooltipProvider = TooltipPrimitive.Provider

export type TooltipProps = TestIdProps & {
    content: string
    children: ReactElement
    side?: 'top' | 'right' | 'bottom' | 'left'
    /**
     * Keeps the tooltip shut without removing it from the tree.
     *
     * For a caller that only knows at run time whether there is anything to
     * say. Unmounting the tooltip instead would remount its child, and a child
     * that is measured to decide the answer would then measure a different
     * element and never settle. Base UI's own `disabled`, so the open state
     * stays uncontrolled and never switches between the two.
     */
    isDisabled?: boolean
}

export const Tooltip = ({
    content,
    children,
    side = 'top',
    isDisabled = false,
    'data-testid': testId
}: TooltipProps) => {
    const contentId = useId()

    return (
        <TooltipPrimitive.Root disabled={isDisabled}>
            {/* The description points at an element that only exists while the
                tooltip is open. That is the standard pattern: a dangling
                aria-describedby is ignored, and the description appears exactly
                when the tooltip does. */}
            <TooltipPrimitive.Trigger aria-describedby={contentId} render={children} />
            <TooltipPrimitive.Portal>
                <TooltipPrimitive.Positioner side={side} sideOffset={6} className={OVERLAY_LAYER}>
                    <TooltipPrimitive.Popup
                        id={contentId}
                        data-testid={testId}
                        role="tooltip"
                        /*
                         * The pointer events stop here.
                         *
                         * The popup renders in a portal, but React sends events
                         * up the React tree rather than the DOM tree. A tooltip
                         * on a cell of a clickable row is a child of that row in
                         * the React tree, so selecting the text inside it also
                         * opened the row, and the reader lost the page. Rows
                         * that navigate on press never see the click, so
                         * `onPointerDown` and `onMouseDown` stop as well.
                         * `CopyUnderline` stops the same three for the same
                         * reason.
                         */
                        onPointerDown={(event) => event.stopPropagation()}
                        onMouseDown={(event) => event.stopPropagation()}
                        onClick={(event) => event.stopPropagation()}
                        // `foreground-onEmphasis`, not `foreground-brand`. The latter is the
                        // mint accent, which is brand colour on a brand surface and reads
                        // as a link rather than as a sentence. A tooltip is a plain
                        // sentence on a dark chip, which is what onEmphasis is for.
                        className="bg-background-brand text-foreground-onEmphasis max-w-64 rounded-aria-md px-2 py-1 text-aria-xs leading-normal shadow-aria-lg"
                    >
                        {content}
                        <TooltipPrimitive.Arrow className="fill-background-brand" />
                    </TooltipPrimitive.Popup>
                </TooltipPrimitive.Positioner>
            </TooltipPrimitive.Portal>
        </TooltipPrimitive.Root>
    )
}
